import { useState } from 'react';
import { useUnmount } from 'react-use';
import { useNavigate, useParams } from 'react-router-dom';
import { MdOutlineChevronLeft, MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { IconButton, Spinner } from '@ds';
import { InfoBanner } from '@ui';
import { ResultsList, MultiSuggestStatistics, MultiSuggestReviewsList } from '@lp/features/MultiSuggest';
import { useMultiSuggestionItemQuery } from '@/queries/multi-suggest';
import { removeMultiSuggestItemCache } from '@/utils/helpers/queryCache.helpers';
import { SuggestionStatus } from '@/api/apis/multi-suggest';

export const MultiCaseSuggestResults = () => {
  const params = useParams<{ multiSuggestId: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useMultiSuggestionItemQuery(params.multiSuggestId);
  const [chainNumber, setChainNumber] = useState(0);

  useUnmount(() => {
    if (data) {
      removeMultiSuggestItemCache(
        params.multiSuggestId || '',
        data.caseList.map((i) => i.caseId) || [],
        data.logistics.map((i) => i.logisticsId) || [],
      );
    }
  });

  const goBackToList = () => navigate('/multi-case-suggest');

  if (isLoading) {
    return (
      <div className="flex flex-col grow place-items-stretch gap-4 p-6 bg-base-70">
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <Spinner size="md" />
        </div>
      </div>
    );
  }

  if (!params.multiSuggestId || !data || !data.caseList.length || !data.logistics.length) {
    return (
      <div className="flex flex-col grow place-items-stretch gap-4 p-6 bg-base-70">
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <span className="text-sm text-neutral-500">Nothing found</span>
        </div>
      </div>
    );
  }

  const isFailed = data.suggestionStatus === SuggestionStatus.Failed;
  const isFinished = data.suggestionStatus === SuggestionStatus.Finished;
  const isInProgress = data.suggestionStatus === SuggestionStatus.InProgress;
  const hasSuggestedInfo = data.chainsCount > 0;
  const showReview = isFinished && hasSuggestedInfo;
  const warnings = (data.warnings || []).join('');

  return (
    <div className="flex w-full h-full flex-col gap-4 py-4 pl-4 bg-base-70">
      <div className="flex gap-4 justify-between items-stretch">
        <div className="flex flex-col gap-4 w-full overflow-hidden">
          <div className="flex gap-2 items-center">
            <IconButton size="xs" startContent={<MdOutlineChevronLeft size={18} />} onPress={goBackToList} />
            <h2 className="text-hm font-secondary text-base-20">Multi-Case Suggestion</h2>
          </div>
          {isFailed && (
            <InfoBanner iconType="outlineWarning">
              The Multi-Case Suggestion processing failed. Please go back and start a new Multi‑Case Suggestion.
            </InfoBanner>
          )}
          {isFinished && !hasSuggestedInfo && !!warnings && (
            <InfoBanner iconType="outlineWarning">{warnings}</InfoBanner>
          )}
          {hasSuggestedInfo && (
            <div className="flex gap-4 items-center">
              <span className="text-hl text-base-20 font-secondary">{`Option ${hasSuggestedInfo ? chainNumber + 1 : 0} of ${data.chainsCount}`}</span>
              <div className="flex gap-2 items-center">
                <IconButton
                  size="md"
                  startContent={<MdOutlineKeyboardArrowLeft size={24} />}
                  isDisabled={chainNumber === 0}
                  onPress={() => setChainNumber((prev) => prev - 1)}
                />
                <IconButton
                  size="md"
                  startContent={<MdOutlineKeyboardArrowRight size={24} />}
                  isDisabled={chainNumber >= data.chainsCount - 1}
                  onPress={() => setChainNumber((prev) => prev + 1)}
                />
              </div>
            </div>
          )}
          <div className="w-full overflow-x-auto">
            <ResultsList taskId={params.multiSuggestId} caseList={data.caseList} chainNumber={chainNumber} />
          </div>
        </div>
        <MultiSuggestStatistics
          taskId={params.multiSuggestId}
          caseList={data.caseList}
          chainNumber={chainNumber}
          isInProgress={isInProgress}
          suggestedDate={data.suggestedDate}
        />
      </div>
      {showReview && <MultiSuggestReviewsList taskId={params.multiSuggestId} />}
    </div>
  );
};
